import { Controller } from "@hotwired/stimulus";

const APPLY_STORAGE_KEY = "technical_sheet_simulation_apply";

// Show-view pricing simulator. Mirrors the edit view's `cost_calculator`
// profitability behaviour (same price <-> coefficient recalculation and the
// same shared profitability panel), but reads a fixed ingredient cost instead
// of summing live form rows, and "applies" values by handing them off to the
// edit form via sessionStorage.
export default class extends Controller {
  static targets = [
    // Input fields
    "grossPriceInput",
    "vatRateInput",
    "wastagePercentageInput",
    "markupCoefficientInput",
    // Fixed values (data-value attributes)
    "totalIngredientCost",
    "portionsCount",
    // Financial summary outputs (Analyse financière card)
    "netPriceOutput",
    "unitCostOutput",
    "unitCostWithWastageOutput",
    "grossMarginOutput",
    "marginRateOutput",
    "profitabilityOutput",
    "markupCoefficientOutput",
    "wastagePercentageOutput",
    "vatRateOutput",
    "wastagePercentageDetailOutput",
    "grossPriceOutput",
    // Shared profitability panel (_profitability_output partial)
    "profitabilityRow",
    "profitabilityBanner",
    "profitabilityIndicator",
    "profitabilityTitle",
    "profitabilityDescription",
    "profitabilityBadge",
    "netPrice",
    "unitCostWithWastage",
    "grossMargin",
    "grossMarginStat",
    "marginRate",
    "marginRateStat",
    "marginRateIndicator",
    "recommendationsPanel",
    "recommendationText",
    // Apply
    "applyPanel",
  ];

  static values = {
    editUrl: String,
    applyToast: { type: String, default: "Valeurs appliquées — n'oubliez pas d'enregistrer" },
  };

  connect() {
    this.baselineValues = null;
    this.recalculate();
    // Capture the baseline after the initial normalisation so the apply panel
    // only appears once the user actually changes something.
    this.baselineValues = this.currentValues();
  }

  recalculate(event) {
    if (event && event.target === this.grossPriceInputTarget) {
      this.updateCoefficientFromPrice();
    } else {
      this.updatePriceFromCoefficient();
    }

    const calculated = this.performCalculations();
    this.updateFinancialOutputs(calculated);
    this.updateProfitabilityDisplay(calculated);
    this.updateApplyPanel();
  }

  // --- Input <-> input derivations (mirror cost_calculator) ---

  updatePriceFromCoefficient() {
    const unitCostWithWastage = this.unitCostWithWastageValue();
    const markupCoefficient = this.parseValue(this.markupCoefficientInputTarget.value);
    const vatRate = this.parseValue(this.vatRateInputTarget.value);

    if (unitCostWithWastage > 0 && markupCoefficient > 0) {
      const netPrice = unitCostWithWastage * markupCoefficient;
      const grossPrice = netPrice * (1 + vatRate / 100);
      this.grossPriceInputTarget.value = grossPrice.toFixed(2);
    }
  }

  updateCoefficientFromPrice() {
    const unitCostWithWastage = this.unitCostWithWastageValue();
    const grossPrice = this.parseValue(this.grossPriceInputTarget.value);
    const vatRate = this.parseValue(this.vatRateInputTarget.value);

    if (unitCostWithWastage > 0 && grossPrice > 0) {
      const netPrice = grossPrice / (1 + vatRate / 100);
      const markupCoefficient = netPrice / unitCostWithWastage;
      this.markupCoefficientInputTarget.value = markupCoefficient.toFixed(2);
    }
  }

  // --- Calculations ---

  getTotalIngredientCost() {
    return this.parseValue(this.totalIngredientCostTarget.dataset.value);
  }

  getPortionsCount() {
    return parseInt(this.portionsCountTarget.dataset.value) || 1;
  }

  unitCostWithWastageValue() {
    const totalIngredientCost = this.getTotalIngredientCost();
    const portionsCount = this.getPortionsCount();
    const wastagePercentage = this.parseValue(this.wastagePercentageInputTarget.value);
    const unitCost = portionsCount > 0 ? totalIngredientCost / portionsCount : 0;
    return unitCost * (1 + wastagePercentage / 100);
  }

  performCalculations() {
    const grossPrice = this.parseValue(this.grossPriceInputTarget.value);
    const vatRate = this.parseValue(this.vatRateInputTarget.value);
    const wastagePercentage = this.parseValue(this.wastagePercentageInputTarget.value);
    const markupCoefficient = this.parseValue(this.markupCoefficientInputTarget.value);

    const totalIngredientCost = this.getTotalIngredientCost();
    const portionsCount = this.getPortionsCount();
    const unitCost = portionsCount > 0 ? totalIngredientCost / portionsCount : 0;
    const unitCostWithWastage = unitCost * (1 + wastagePercentage / 100);
    const netPrice = grossPrice / (1 + vatRate / 100);
    const grossMargin = netPrice - unitCostWithWastage;
    const marginRate = netPrice > 0 ? (grossMargin / netPrice) * 100 : 0;

    return {
      grossPrice,
      vatRate,
      wastagePercentage,
      markupCoefficient,
      totalIngredientCost,
      unitCost,
      unitCostWithWastage,
      netPrice,
      grossMargin,
      marginRate,
    };
  }

  // --- Financial summary outputs (Analyse financière) ---

  updateFinancialOutputs(c) {
    const marginClass = this.marginValueClass(c.marginRate);

    this.grossPriceOutputTargets.forEach((el) => (el.textContent = this.formatCurrency(c.grossPrice)));
    this.netPriceOutputTargets.forEach((el) => (el.textContent = this.formatCurrency(c.netPrice)));
    this.unitCostOutputTargets.forEach((el) => (el.textContent = this.formatCurrency(c.unitCost)));
    this.unitCostWithWastageOutputTargets.forEach((el) => (el.textContent = this.formatCurrency(c.unitCostWithWastage)));
    this.grossMarginOutputTargets.forEach((el) => {
      el.textContent = this.formatCurrency(c.grossMargin);
      this.applyMarginClass(el, marginClass);
    });
    this.marginRateOutputTargets.forEach((el) => (el.textContent = `${this.formatNumber(c.marginRate)} % de marge`));
    this.markupCoefficientOutputTargets.forEach((el) => (el.textContent = `Marge × ${this.formatNumber(c.markupCoefficient)}`));
    this.profitabilityOutputTargets.forEach((el) => {
      el.textContent = this.getProfitabilityLevel(c.marginRate).label;
      this.applyMarginClass(el, marginClass);
    });
    this.wastagePercentageOutputTargets.forEach((el) => (el.textContent = `Perte de ${this.formatNumber(c.wastagePercentage)} % incluse`));
    this.vatRateOutputTargets.forEach((el) => (el.textContent = this.formatPercent(c.vatRate)));
    this.wastagePercentageDetailOutputTargets.forEach((el) => (el.textContent = this.formatPercent(c.wastagePercentage)));
  }

  // --- Shared profitability panel (identical to cost_calculator) ---

  updateProfitabilityDisplay(c) {
    if (!this.hasProfitabilityBannerTarget) return;

    if (c.totalIngredientCost <= 0) {
      this.hideProfitabilitySystem();
      return;
    }

    this.netPriceTargets.forEach((el) => (el.textContent = this.formatCurrency(c.netPrice)));
    this.unitCostWithWastageTargets.forEach((el) => (el.textContent = this.formatCurrency(c.unitCostWithWastage)));
    this.grossMarginTargets.forEach((el) => (el.textContent = this.formatCurrency(c.grossMargin)));
    this.marginRateTargets.forEach((el) => (el.textContent = `${c.marginRate.toFixed(1)}%`));

    const profitability = this.getProfitabilityLevel(c.marginRate);
    this.updateProfitabilityBanner(profitability, c.marginRate);
    this.updateStatBoxes(profitability, c.grossMargin);
    this.updateRecommendations(profitability, c.unitCostWithWastage);
  }

  getProfitabilityLevel(marginRate) {
    if (marginRate >= 70) {
      return { level: "excellent", label: "Excellente", description: "🚀 Recette très rentable ! Marge exceptionnelle", color: "success", bgColor: "bg-success/10", borderColor: "border-success/30", textColor: "text-success" };
    } else if (marginRate >= 50) {
      return { level: "very-good", label: "Très bonne", description: "✨ Très bonne rentabilité, continuez ainsi !", color: "success", bgColor: "bg-success/10", borderColor: "border-success/30", textColor: "text-success" };
    } else if (marginRate >= 30) {
      return { level: "good", label: "Bonne", description: "👍 Rentabilité correcte, peut être optimisée", color: "warning", bgColor: "bg-warning/10", borderColor: "border-warning/30", textColor: "text-warning" };
    } else if (marginRate >= 10) {
      return { level: "fair", label: "Correcte", description: "⚠️ Marge faible, surveillez vos coûts", color: "warning", bgColor: "bg-warning/10", borderColor: "border-warning/30", textColor: "text-warning" };
    } else if (marginRate >= 0) {
      return { level: "poor", label: "Faible", description: "⚡ Marge très faible, action requise", color: "error", bgColor: "bg-error/10", borderColor: "border-error/30", textColor: "text-error" };
    }
    return { level: "deficit", label: "Déficitaire", description: "🔴 Recette déficitaire ! Révision urgente", color: "error", bgColor: "bg-error/10", borderColor: "border-error/30", textColor: "text-error" };
  }

  updateProfitabilityBanner(profitability, marginRate) {
    this.profitabilityBannerTarget.className = `mb-4 p-3 rounded-lg border-l-4 transition-all duration-500 ${profitability.bgColor} ${profitability.borderColor}`;
    this.profitabilityIndicatorTarget.className = `w-3 h-3 rounded-full animate-pulse bg-${profitability.color}`;
    this.profitabilityTitleTarget.textContent = `Rentabilité : ${profitability.label} (${marginRate.toFixed(1)}%)`;
    this.profitabilityTitleTarget.className = `font-semibold text-sm ${profitability.textColor}`;
    this.profitabilityDescriptionTarget.textContent = profitability.description;
    this.profitabilityBadgeTarget.textContent = profitability.label;
    this.profitabilityBadgeTarget.className = `badge badge-${profitability.color}`;
    this.profitabilityRowTarget.className = "transition-all duration-500";
  }

  updateStatBoxes(profitability, grossMargin) {
    const statBase = "flex flex-col items-center justify-center gap-1 text-center rounded-xl p-4 border transition-all duration-300";
    if (grossMargin > 0) {
      this.grossMarginStatTarget.className = `${statBase} border-success/30 ${profitability.bgColor}`;
    } else {
      this.grossMarginStatTarget.className = `${statBase} border-error/30 bg-error/5`;
    }
    this.marginRateStatTarget.className = `${statBase} border-base-300 ${profitability.bgColor}`;
    this.marginRateIndicatorTarget.className = `w-2 h-2 rounded-full bg-${profitability.color}`;
  }

  updateRecommendations(profitability, unitCost) {
    let recommendation;
    switch (profitability.level) {
      case "excellent":
        recommendation = "Félicitations ! Votre recette est très rentable. Vous pourriez même légèrement réduire le prix pour être plus compétitif.";
        break;
      case "very-good":
        recommendation = "Très bien ! Maintenez cette rentabilité en surveillant vos coûts d'approvisionnement.";
        break;
      case "good":
        recommendation = "Bonne marge. Essayez d'optimiser vos achats ou d'ajuster légèrement votre prix de vente.";
        break;
      case "fair":
        recommendation = `Marge faible. Considérez augmenter votre prix ou réduire vos coûts. Coût actuel: ${this.formatCurrency(unitCost)}`;
        break;
      case "poor":
        recommendation = "Marge critique ! Révisez vos fournisseurs, portions ou augmentez significativement votre prix.";
        break;
      default:
        recommendation = "🚨 Attention ! Cette recette vous fait perdre de l'argent. Action immédiate requise sur le prix ou les coûts.";
    }
    this.recommendationTextTarget.textContent = recommendation;
    this.recommendationsPanelTarget.style.display = "block";
  }

  hideProfitabilitySystem() {
    this.profitabilityBannerTarget.className = "mb-4 p-3 rounded-lg border-l-4 transition-all duration-500 bg-base-100 border-base-300";
    this.profitabilityIndicatorTarget.className = "w-3 h-3 rounded-full bg-base-300";
    this.profitabilityTitleTarget.textContent = "Ajoutez des ingrédients pour calculer la rentabilité";
    this.profitabilityTitleTarget.className = "font-semibold text-sm text-base-content/60";
    this.profitabilityDescriptionTarget.textContent = "Commencez par ajouter des ingrédients ou sous-recettes";
    this.profitabilityBadgeTarget.textContent = "-";
    this.profitabilityBadgeTarget.className = "badge badge-ghost";
    this.profitabilityRowTarget.className = "transition-all duration-500";

    const statBase = "flex flex-col items-center justify-center gap-1 text-center rounded-xl p-4 border border-base-300 bg-base-200/40 transition-all duration-300";
    this.grossMarginStatTarget.className = statBase;
    this.marginRateStatTarget.className = statBase;
    this.marginRateIndicatorTarget.className = "w-2 h-2 rounded-full bg-base-300";
    this.recommendationsPanelTarget.style.display = "none";
  }

  // --- Apply (hand off to the edit form) ---

  updateApplyPanel() {
    if (!this.hasApplyPanelTarget || !this.baselineValues) return;

    const current = this.currentValues();
    const dirty = Object.keys(current).some((key) => current[key] !== this.baselineValues[key]);
    this.applyPanelTarget.classList.toggle("hidden", !dirty);
  }

  currentValues() {
    return {
      gross_price: this.grossPriceInputTarget.value,
      vat_rate: this.vatRateInputTarget.value,
      wastage_percentage: this.wastagePercentageInputTarget.value,
      markup_coefficient: this.markupCoefficientInputTarget.value,
    };
  }

  applyToSheet() {
    const payload = this.currentValues();
    sessionStorage.setItem(APPLY_STORAGE_KEY, JSON.stringify(payload));

    if (this.hasEditUrlValue && this.editUrlValue) {
      window.Turbo.visit(this.editUrlValue);
      return;
    }

    this.baselineValues = payload;
    this.updateApplyPanel();
    this.showToast();
  }

  showToast() {
    const alert = document.createElement("div");
    alert.className = "alert alert-success shadow-lg fixed bottom-4 right-4 z-50 max-w-sm";
    alert.setAttribute("role", "status");
    alert.innerHTML = `<span>${this.applyToastValue}</span>`;
    document.body.appendChild(alert);
    setTimeout(() => alert.remove(), 5000);
  }

  // --- Helpers ---

  applyMarginClass(el, marginClass) {
    el.classList.remove("text-primary", "text-secondary", "text-accent", "text-success", "text-error", "text-base-content");
    el.classList.add(marginClass);
  }

  marginValueClass(marginRate) {
    if (marginRate <= 0) return "text-error";
    if (marginRate >= 30) return "text-success";
    return "text-base-content";
  }

  formatCurrency(value) {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value || 0);
  }

  formatPercent(value) {
    return new Intl.NumberFormat("fr-FR", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format((value || 0) / 100);
  }

  formatNumber(value, digits = 2) {
    return new Intl.NumberFormat("fr-FR", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value || 0);
  }

  parseValue(value) {
    if (typeof value !== "string") return value || 0;
    let cleanValue = value.replace(/[€\s]/g, "").replace(/[^\d,.-]/g, "");
    if (cleanValue.includes(",")) {
      cleanValue = cleanValue.includes(".")
        ? cleanValue.replace(/\./g, "").replace(",", ".")
        : cleanValue.replace(",", ".");
    }
    const result = parseFloat(cleanValue);
    return isNaN(result) ? 0 : result;
  }
}

export { APPLY_STORAGE_KEY };
